/**
 * MinIO 私有桶 → 签名 URL 换票工具
 *
 * 背景：
 *   - 后端 OSS 桶 ruoyicould 已私有化，永久 URL 直接给 <img src> 会被 403
 *   - <img> 标签无法携带 Authorization 请求头，必须经登录态接口 "换票"
 *     获取短时效（默认 600s）签名 URL 再展示
 *   - 后端换票接口：POST /shltyy/file/presign
 *     请求体：URL 字符串数组（≤50 条）
 *     响应：{ code, data: { 原URL: 签名URL, ... } }
 *   - dev 环境 vue.config.js 已配 /shltyy → https://shltyy.jsltpct.com:9526
 *   - 生产需 nginx 同样转发 /shltyy （参考换票文档 minio-presign-frontend-integration.md）
 *
 * 参考：
 *   - ruoyi-coloud-app/src/api/file.ts (mini-program 同款实现)
 *   - ltshwechat/minio-presign-frontend-integration.md （换票接口规范）
 */
import axios from 'axios';
import { getToken } from '@/utils/auth';

// 原 URL -> { signed, expireAt }
const cache = new Map();
// 提前 30 秒视为过期，避免渲染时恰好失效
const CACHE_AHEAD_MS = 30 * 1000;
// 与后端默认签名有效期对齐（600s）；若后端 nacos 调整过，此处同步调整
const SIGN_TTL_MS = 10 * 60 * 1000;
// 单次换票上限
const BATCH_LIMIT = 50;
// 本桶标识：仅含该路径的 URL 需要换票
// 注意：后端文档示例为 ruoyicould-prod，但实际接口返回为 ruoyicould，此处兼容两种
const BUCKET_MARKER = /\/upload\/ruoyicould(?:-prod)?\//i;

/**
 * 判断 URL 是否需要换票
 * - 空值/非字符串：不需要
 * - 非 http(s)（blob / data: / 本地临时文件）：不需要
 * - 非本桶地址（旧图床、外链、示例图）：不需要
 */
function needsPresign(url) {
  if (!url || typeof url !== 'string') return false;
  if (!/^https?:\/\//i.test(url)) return false;
  return BUCKET_MARKER.test(url);
}

/**
 * 将数组按指定大小分批
 */
function chunkArray(arr, size) {
  const out = [];
  for (let i = 0; i < arr.length; i += size) out.push(arr.slice(i, i + size));
  return out;
}

/**
 * 批量换取图片预签名 URL
 *
 * @param {string[]} urls 库中存储的完整 URL 列表（≤50，超过会自动分批）
 * @returns {Promise<Object<string,string>>} 原URL -> 可展示URL 的映射
 *          非本桶地址会原样映射；失败也兜底映射原 URL
 */
export async function presignImages(urls) {
  const now = Date.now();
  const need = [];
  const result = {};

  for (const url of urls || []) {
    if (!url) continue;
    // 非本桶地址（旧图床、外链、本地临时文件）直接透传，无需换票
    if (!needsPresign(url)) {
      result[url] = url;
      continue;
    }
    const hit = cache.get(url);
    if (hit && hit.expireAt > now + CACHE_AHEAD_MS) {
      result[url] = hit.signed;
    } else {
      need.push(url);
    }
  }

  // 超过上限则分批调用
  const batches = chunkArray(need, BATCH_LIMIT);
  for (const batch of batches) {
    if (!batch.length) continue;
    try {
      // 兼容后端多种 token 读取方式：
      //   1) Authorization: Bearer <登录token>（标准 JWT）
      //   2) header: dhyytoken
      //   3) query: ?dhyytoken=...
      // 后端说"令牌不能为空"，但不确定读的是哪个位置，先全部带上让后端 whichever 可用。
      const dhyyToken = process.env.VUE_APP_DHYY_TOKEN || '';
      const loginToken = getToken() || '';
      const headers = { 'Content-Type': 'application/json' };
      const params = {};
      if (dhyyToken) {
        headers.dhyytoken = dhyyToken;
        params.dhyytoken = dhyyToken;
      }
      if (loginToken) {
        headers.Authorization = `Bearer ${loginToken}`;
      }
      console.log('[presignImages] 发送请求 tokens:', {
        url: batch[0],
        hasDhyyToken: !!dhyyToken,
        hasLoginToken: !!loginToken,
        headers: Object.keys(headers),
        params: Object.keys(params),
      });
      const res = await axios.post('/shltyy/file/presign', batch, {
        timeout: 30000,
        headers,
        params,
      });
      // 兼容多种响应包装：
      //   A) { code: 200, data: { 原URL: 签名URL, ... } }  ← 后端标准 R<T>
      //   B) { data: { 原URL: 签名URL, ... } }            ← 简化包装
      //   C) { 原URL: 签名URL, ... }                      ← 最简
      let map = {};
      if (res && res.data) {
        if (res.data.data && typeof res.data.data === 'object' && !Array.isArray(res.data.data)) {
          map = res.data.data;
        } else if (typeof res.data === 'object' && !Array.isArray(res.data)) {
          map = res.data;
        }
      }
      console.log('[presignImages] 响应:', res && res.data);
      for (const k of Object.keys(map)) {
        const v = map[k];
        if (typeof v === 'string' && v) {
          cache.set(k, { signed: v, expireAt: now + SIGN_TTL_MS });
          result[k] = v;
          // 诊断：如果后端返回的仍是原 URL，说明换票未生效
          if (v === k) console.warn('[presignImages] 后端返回了原 URL（未签名）:', k);
        }
      }
      // 后端未返回或未正确解析的兜底：原 URL（不会让 imgLoadFailed 雪崩）
      for (const url of batch) {
        if (!(url in result)) result[url] = url;
      }
    } catch (err) {
      // 换票失败兜底：原 URL 仍可显示（桶私有化前兼容）
      for (const url of batch) result[url] = url;
      console.error('[presignImages] 批量换票失败', err);
    }
  }

  return result;
}

/**
 * 单 URL 便捷方法（内部走 presignImages，同页多图请直接用 presignImages 批量）
 */
export async function presignImage(url) {
  if (!url) return url;
  const map = await presignImages([url]);
  return map[url] || url;
}

export default { presignImages, presignImage };
